import apiProxyClient from '@libs/api-proxy-client';
import { AlgoliaIndice, getObjectById } from '@libs/algolia';

import type { Store } from '@libs/common-node';
import type { Nullable } from '@libs/core';
import { stringifyStoreId, logger } from '@libs/core';

const getAlgoliaStore = async (storeId: string) => {
    try {
        return (
            (await getObjectById(
                stringifyStoreId(storeId) ?? '',
                AlgoliaIndice.STORES
            )) ?? {}
        );
    } catch (algoliaError) {
        logger.error('[getAlgoliaStore]: Error fetching Algolia store data:', {
            error: algoliaError,
            storeId,
        });
    }
    return null;
};

/** * Fetches store data by combining information from both the API and Algolia search. * *
 * @param storeId - The unique identifier of the store. Can be null/undefined. *
 * @returns Promise that resolves to: *  - null if storeId is invalid or API fetch fails *
 *  - Store object with API data if Algolia fetch fails *
 * - Store object with merged API and Algolia data if both succeed
 * * @throws Never - All errors are caught and logged internally */

const getStoreById = async (
    storeId?: Nullable<string>
): Promise<Store | null> => {
    if (!storeId) {
        return null;
    }
    try {
        const store = await apiProxyClient.stores.getStoreById({ storeId });

        if (!store) {
            return null;
        }
        const formattedStoreId = stringifyStoreId(storeId);
        if (!formattedStoreId) {
            // If ID formatting fails, return just the API store data
            return store;
        }
        const algoliaStore = await getAlgoliaStore(formattedStoreId);

        return algoliaStore ? { ...store, ...algoliaStore } : store;
    } catch (error) {
        logger.error('[getStoreById]: Error fetching store data:', {
            error,
            storeId,
        });
    }
    return null;
};

export { getStoreById };
